import { Box, Modal, Button, Autocomplete, TextField, TextFieldProps, Typography, useTheme, useMediaQuery, Stack, FilterOptionsState } from '@mui/material';
import { Building02 } from '../../../../../assets';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../../../shared/useUser';
import { Store, Franchise, System } from '../../../../../app/services/api/generated';
import { useTranslation } from 'react-i18next';
import { ThemeContext } from '../../../../../theme/context/themeProviderContext';
import useNswagClient from '../../../../../hooks/api/useNswagClient';
import useLogError from '../../../../../hooks/useLogError';
import { transformForTranslation } from '../../../../../pages/orderly-for-distributors/ManageRoles/components/RolesBox';

const textClamp = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: '1',
  WebkitBoxOrient: 'vertical',
};

interface SelectStoreModalProps {
  open: boolean,
  onConfirm: () => void,
  onClose: () => void,
}

export const SelectStoreModal = ({ onConfirm, onClose, open }: SelectStoreModalProps) => {
  const { user, selectedFranchise, setSelectedStore, setSelectedSystem, setSelectedFranchise } = useContext(UserContext);
  const [newStore, setNewStore] = useState<Store | null>(null);
  const [newFranchise, setNewFranchise] = useState<Franchise | null>(null);
  const [newSystem, setNewSystem] = useState<System | null>(null);
  const theme = useTheme();
  const { t } = useTranslation('common');
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const { getBrandingForFranchise } = useNswagClient();
  const [, changeBranding] = useContext(ThemeContext);
  const { logError } = useLogError();

  const [showSystemDropdown, setShowSystemDropdown] = useState<boolean>(true);
  const [showFranchiseDropdown, setShowFranchiseDropdown] = useState<boolean>(false);
  const [showStoreDropdown, setShowStoreDropdown] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setNewStore(null);
      setNewFranchise(null);
      setNewSystem(null);
      initializeDropdowns();
    }
  }, [open]);

  const initializeDropdowns = () => {
    const systems = user?.systems?.filter(s => s.id !== 0) ?? [];

    if (systems.length === 1) {
      const singleSystem = systems[0];
      setNewSystem(singleSystem);
      setShowSystemDropdown(false);

      const systemFranchises = user?.franchises?.filter(f =>
        f.systems?.some(s => s.id === singleSystem.id) &&
        user?.stores?.some(s => s.franchiseId === f.franchiseId)) || [];

      if (systemFranchises.length === 1) {
        const singleFranchise = systemFranchises[0];
        setNewFranchise(singleFranchise);
        setShowFranchiseDropdown(false);

        const franchiseStores = user?.stores?.filter(s => s.franchiseId === singleFranchise.franchiseId) || [];

        if (franchiseStores.length === 1) {
          const singleStore = franchiseStores[0];
          setNewStore(singleStore);
          setShowStoreDropdown(false);
        } else {
          setShowStoreDropdown(true);
        }
      } else {
        setShowFranchiseDropdown(true);
      }
    } else {
      setShowSystemDropdown(true);
    }
  };

  const updateStore = ((store: Store, franchise: Franchise, system: System) => {
    setSelectedStore({ ...store, franchiseName: franchise.franchiseName ?? store.franchiseName });
    setSelectedFranchise(franchise);
    setSelectedSystem(system);
    onConfirm();
    if (store?.franchiseId && franchise.franchiseId !== selectedFranchise?.franchiseId) {
      updateBranding(store.franchiseId);
    }
  });

  const updateBranding = ((id: number) => {
    getBrandingForFranchise(id)
      .then((result) => {
        if (result?.data)
          changeBranding(result.data);

        if (!result)
          changeBranding(null);
      })
      .catch((error) => {
        logError(error);
      });
  });

  const filter = (options: Store[], state: FilterOptionsState<Store>) => {
    return options.filter((option) => option.storeNumber?.startsWith(state.inputValue) || option.storeName?.toLowerCase().includes(state.inputValue.toLowerCase()));
  };

  useEffect(() => {
    if (newSystem) {
      const systemFranchises = user?.franchises?.filter(f =>
        f.systems?.some(s => s.id === newSystem.id) &&
        user?.stores?.some(s => s.franchiseId === f.franchiseId)) || [];

      setShowFranchiseDropdown(systemFranchises.length > 0);

      if (systemFranchises.length === 1) {
        handleChangeSystem(newSystem);
      }
    }
  }, [newSystem]);

  useEffect(() => {
    if (newFranchise) {
      const franchiseStores = user?.stores?.filter(s => s.franchiseId === newFranchise.franchiseId) || [];
      setShowStoreDropdown(franchiseStores.length > 0);

      if (franchiseStores.length === 1 && !newStore) {
        setNewStore(franchiseStores[0]);
      }
    }
  }, [newFranchise]);

  const handleChangeSystem = (system: System) => {
    if (system) {
      setNewSystem(system);
      setNewFranchise(null);
      setNewStore(null);

      const systemFranchises = user?.franchises?.filter(f =>
        f.systems?.some(s => s.id === system.id) &&
        user?.stores?.some(s => s.franchiseId === f.franchiseId)) || [];

      if (systemFranchises.length === 1) {
        handleChangeFranchise(systemFranchises[0]);
        setShowFranchiseDropdown(false);
      } else {
        setShowFranchiseDropdown(true);
        setShowStoreDropdown(false);
      }
    }
  };

  const handleChangeFranchise = (franchise: Franchise) => {
    if (franchise) {
      setNewFranchise(franchise);
      setNewStore(null);

      const franchiseStores = user?.stores?.filter(s => s.franchiseId === franchise.franchiseId) || [];
      if (franchiseStores.length === 1) {
        setNewStore(franchiseStores[0]);
        setShowStoreDropdown(false);
      } else {
        setShowStoreDropdown(true);
      }
    }
  };

  return (<Modal
    open={open}
    onClose={onClose}
  >
    <Stack
      position='absolute'
      gap={9}
      top='50%'
      left='50%'
      minHeight='200px'
      bgcolor='background.paper'
      border='1px solid grey'
      boxShadow='24px'
      p={6}
      width={`${isMobile ? '300px' : '600px'}`}
      sx={{
        transform: 'translate(-50%, -50%)',
      }}>
      <Box
        display='flex'
        flexDirection='row'
        alignItems='center'
        gap={6}>
        <Building02 />
        <Typography id="modal-modal-title">
          {t('selectAStore')}
        </Typography>
      </Box>

      {showSystemDropdown && (
        <Autocomplete
          onChange={(event, newValue: System | null) => {
            if (newValue) {
              handleChangeSystem(newValue);
            }
          }}
          id="select-system"
          options={user?.systems?.filter(s => s.id !== 0) ?? []}
          sx={{ width: '100%', overflow: 'auto', pt: 4 }}
          getOptionLabel={(option: System) => option.name ?? ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterOptions={(options: System[], state) => options.filter((option) => option.name?.toLowerCase().includes(state.inputValue.toLowerCase()))}
          renderOption={(props, option) => (
            <Box component="li"
              sx={{ '& > img': { mr: 2, flexShrink: 0 }, gap: 2 }}
              {...props}
              key={option.id}>
              <Building02 style={{ minWidth: '24px' }} />
              <Typography
                variant='textSM'
                sx={{
                  ...textClamp,
                }}>
                {t(transformForTranslation(option.name))}
              </Typography>
            </Box>
          )}
          renderInput={(params) => {
            return <TextField placeholder={t('selectSystem')}
              {...params as TextFieldProps} />;
          }}
          value={newSystem} />
      )}

      {showFranchiseDropdown && (
        <Autocomplete
          onChange={(event, newValue: Franchise | null) => {
            if (newValue) {
              handleChangeFranchise(newValue);
            }
          }}
          id="select-franchise"
          options={newSystem
            ? (user?.franchises?.filter(franchise => {
              if (!franchise.systems || franchise.systems.length === 0) {
                return false;
              }

              if (!user?.stores ||
                user?.stores.length === 0 ||
                !user?.stores?.some(s => s.franchiseId === franchise.franchiseId || s.franchiseName?.toLocaleLowerCase() == franchise.franchiseName?.toLocaleLowerCase())) {
                return false;
              }

              return franchise.systems.some(system => {
                if (!system.id || !newSystem.id) return false;
                return system.id === newSystem.id;
              });
            }) ?? [])
            : []}
          sx={{ width: '100%', overflow: 'auto', pt: 4 }}
          getOptionLabel={(option: Franchise) => option.franchiseName ?? ''}
          isOptionEqualToValue={(option, value) => option.franchiseId == value.franchiseId}
          filterOptions={(options: Franchise[], state) => options.filter((option) => option.franchiseName?.toLowerCase().includes(state.inputValue.toLowerCase()))}
          renderOption={(props, option) => (
            <Box component="li"
              sx={{ '& > img': { mr: 2, flexShrink: 0 }, gap: 2 }}
              {...props}
              key={option.franchiseId}>
              <Building02 style={{ minWidth: '24px' }} />
              <Typography
                variant='textSM'
                sx={{
                  ...textClamp,
                }}>
                {option.franchiseName}
              </Typography>
            </Box>
          )}
          renderInput={(params) => {
            return <TextField placeholder={t('selectFranchise')}
              {...params as TextFieldProps} />;
          }}
          value={newFranchise} />
      )}

      {showStoreDropdown && (
        <Autocomplete
          onChange={(event, newValue: Store | null) => {
            if (newValue) {
              setNewStore(newValue);
            }
          }}
          id="combo-box-demo"
          options={user?.stores?.filter(s => s.franchiseId == newFranchise?.franchiseId) ?? []}
          sx={{ width: '100%', overflow: 'auto', pt: 4 }}
          getOptionLabel={(option: Store) => option.storeName ?? ''}
          isOptionEqualToValue={(option, value) => option.storeNumber === value.storeNumber}
          filterOptions={(options, state) => filter(options, state)}
          renderOption={(props, option) => (
            <Box component="li"
              sx={{ '& > img': { mr: 2, flexShrink: 0 }, gap: 2 }}
              {...props}
              key={(option?.storeName ?? '') + (option?.storeNumber ?? '')}>
              <Building02 style={{ minWidth: '24px' }} />
              <Typography
                variant='textSM'
                sx={{
                  ...textClamp,
                }}>
                {option.storeName}
              </Typography>
            </Box>
          )}
          renderInput={(params) => {
            return <TextField placeholder={t('selectAStore')}
              {...params as TextFieldProps} />;
          }}
          value={newStore} />
      )}

      <Box display='flex'
        flexDirection='row'
        justifyContent='center'
        gap={9}>
        <Button variant='secondary'
          onClick={onClose}
          type='button'>{t('dismiss')}</Button>
        <Button
          disabled={!newStore || !newFranchise || !newSystem}
          onClick={() => {
            if (newStore && newFranchise && newSystem) {
              updateStore(newStore, newFranchise, newSystem);
            }
          }}
          type='button'
        >
          {t('confirm')}</Button>
      </Box>
    </Stack>
  </Modal>);
};